import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface DemoModule {
  id: string;
  key: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  category: string;
  isEnabled: boolean;
  isVisible: boolean;
  sortOrder: number;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  fields?: DemoField[];
  views?: DemoView[];
  _count?: {
    fields: number;
    workflows: number;
  };
}

export interface DemoField {
  id: string;
  moduleId: string;
  key: string;
  label: string;
  dataType: 'string' | 'number' | 'date' | 'boolean' | 'reference' | 'choice';
  fieldType: 'text' | 'textarea' | 'select' | 'multiselect' | 'date' | 'datetime' | 'number' | 'checkbox';
  isRequired: boolean;
  isUnique: boolean;
  isReadonly: boolean;
  options?: { value: string; label: string }[];
  referenceModule?: string;
  sortOrder: number;
  isVisible: boolean;
  isSearchable: boolean;
  inListView: boolean;
  inCreateForm: boolean;
  inEditForm: boolean;
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  pattern?: string;
  defaultValue?: string;
  metadata?: any;
}

export interface DemoView {
  id: string;
  moduleId: string;
  key: string;
  name: string;
  viewType: 'table' | 'form' | 'calendar' | 'kanban' | 'timeline';
  isDefault: boolean;
  config: any;
}

export interface DemoRecord {
  id: string;
  moduleKey: string;
  data: Record<string, any>;
  status: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

class DemoModulesApi {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Module endpoints
  async getModules(category?: string): Promise<DemoModule[]> {
    const params = category ? { category } : {};
    const response = await axios.get(`${API_BASE_URL}/demo-modules`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getModule(id: string): Promise<DemoModule> {
    const response = await axios.get(`${API_BASE_URL}/demo-modules/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getModuleByKey(key: string): Promise<DemoModule> {
    const response = await axios.get(`${API_BASE_URL}/demo-modules/key/${key}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createModule(data: Partial<DemoModule>): Promise<DemoModule> {
    const response = await axios.post(`${API_BASE_URL}/demo-modules`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateModule(id: string, data: Partial<DemoModule>): Promise<DemoModule> {
    const response = await axios.put(`${API_BASE_URL}/demo-modules/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteModule(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/demo-modules/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async reorderModules(moduleIds: string[]): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/demo-modules/reorder`,
      { moduleIds },
      { headers: this.getAuthHeaders() }
    );
  }

  // Field endpoints
  async getFields(moduleId: string): Promise<DemoField[]> {
    const response = await axios.get(`${API_BASE_URL}/demo-modules/${moduleId}/fields`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createField(moduleId: string, data: Partial<DemoField>): Promise<DemoField> {
    const response = await axios.post(
      `${API_BASE_URL}/demo-modules/${moduleId}/fields`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateField(moduleId: string, fieldId: string, data: Partial<DemoField>): Promise<DemoField> {
    const response = await axios.put(
      `${API_BASE_URL}/demo-modules/${moduleId}/fields/${fieldId}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteField(moduleId: string, fieldId: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/demo-modules/${moduleId}/fields/${fieldId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async reorderFields(moduleId: string, fieldIds: string[]): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/demo-modules/${moduleId}/fields/reorder`,
      { fieldIds },
      { headers: this.getAuthHeaders() }
    );
  }

  // Record endpoints
  async getRecords(
    moduleKey: string,
    options?: {
      status?: string;
      limit?: number;
      skip?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<{ records: DemoRecord[]; total: number; limit: number; skip: number }> {
    const response = await axios.get(`${API_BASE_URL}/demo/${moduleKey}/records`, {
      headers: this.getAuthHeaders(),
      params: options,
    });
    return response.data;
  }

  async getRecord(moduleKey: string, id: string): Promise<DemoRecord> {
    const response = await axios.get(`${API_BASE_URL}/demo/${moduleKey}/records/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createRecord(moduleKey: string, data: Record<string, any>): Promise<DemoRecord> {
    const response = await axios.post(
      `${API_BASE_URL}/demo/${moduleKey}/records`,
      { data },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateRecord(moduleKey: string, id: string, data: Record<string, any>): Promise<DemoRecord> {
    const response = await axios.put(
      `${API_BASE_URL}/demo/${moduleKey}/records/${id}`,
      { data },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteRecord(moduleKey: string, id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/demo/${moduleKey}/records/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async searchRecords(
    moduleKey: string,
    searchTerm: string,
    searchFields: string[]
  ): Promise<{ records: DemoRecord[]; total: number }> {
    const response = await axios.get(`${API_BASE_URL}/demo/${moduleKey}/records/search`, {
      headers: this.getAuthHeaders(),
      params: {
        q: searchTerm,
        fields: searchFields.join(','),
      },
    });
    return response.data;
  }
}

export default new DemoModulesApi();
